/**
 * @file src/api/product.api.js
 * @description Product API calls.
 *
 * Rules:
 * - API layer only.
 * - No React.
 * - No hooks.
 * - No state.
 * - No mock data.
 * - No companyId is sent from frontend.
 * - Active company is resolved by backend from the selected company token/context.
 */

import { apiClient } from "lib/axios";

const normalizeProduct = (product) => {
  if (!product) return product;

  return {
    ...product,
    id: product.id || product._id,
  };
};

const normalizeProductListResponse = (data) => {
  const products = data?.products || data?.data?.products || data?.data || [];

  return {
    ...data,
    products: Array.isArray(products) ? products.map(normalizeProduct) : [],
  };
};

const normalizeProductResponse = (data) => {
  const product = data?.product || data?.data?.product || data?.data || data;

  return {
    ...data,
    product: normalizeProduct(product),
  };
};

export const getProducts = async (params = {}) => {
  const { data } = await apiClient.get("/products", {
    params,
  });

  return normalizeProductListResponse(data);
};

export const getProductById = async (id) => {
  const { data } = await apiClient.get(`/products/${id}`);

  return normalizeProductResponse(data);
};

export const createProduct = async (payload) => {
  const cleanPayload = {
    name: payload.name,
    description: payload.description,
    unitSellingPrice: payload.unitSellingPrice,
    taxRate: payload.taxRate,
    vatCategoryCode: payload.vatCategoryCode,
    exemptionCategory: payload.exemptionCategory,
    exemptionReasonCode: payload.exemptionReasonCode,
    exemptionReason: payload.exemptionReason,
    customsCode: payload.customsCode,
    uniqueCode: payload.uniqueCode,
  };

  const { data } = await apiClient.post("/products", cleanPayload);

  return normalizeProductResponse(data);
};

export const updateProduct = async (id, payload) => {
  const cleanPayload = {
    name: payload.name,
    description: payload.description,
    unitSellingPrice: payload.unitSellingPrice,
    taxRate: payload.taxRate,
    vatCategoryCode: payload.vatCategoryCode,
    exemptionCategory: payload.exemptionCategory,
    exemptionReasonCode: payload.exemptionReasonCode,
    exemptionReason: payload.exemptionReason,
    customsCode: payload.customsCode,
    uniqueCode: payload.uniqueCode,
  };

  const { data } = await apiClient.put(`/products/${id}`, cleanPayload);

  return normalizeProductResponse(data);
};

export const deleteProduct = async (id) => {
  const { data } = await apiClient.delete(`/products/${id}`);

  return data;
};
